import { useEffect, useRef } from 'react';

/**
 * Custom hook to dynamically load external scripts
 * @param src - The URL of the script to load
 * @param onLoad - Callback function to execute when the script loads
 */
export const useScript = (src: string, onLoad?: () => void): void => {
  const onLoadRef = useRef(onLoad);

  // Keep the callback ref updated
  useEffect(() => {
    onLoadRef.current = onLoad;
  }, [onLoad]);

  useEffect(() => {
    // Check if script already exists
    const existingScript = document.querySelector(`script[src="${src}"]`);
    
    if (existingScript) {
      if ((existingScript as HTMLScriptElement).dataset.loaded === 'true') {
        onLoadRef.current?.();
        return;
      }
      const handler = () => onLoadRef.current?.();
      existingScript.addEventListener('load', handler, { once: true });
      return () => {
        existingScript.removeEventListener('load', handler);
      };
    }

    // Create and append the script
    const script = document.createElement('script');
    script.src = src;
    script.async = true;

    const handleLoad = () => {
      script.dataset.loaded = 'true';
      onLoadRef.current?.();
    };

    const handleError = () => {
      console.error(`Failed to load script: ${src}`);
    };

    script.addEventListener('load', handleLoad);
    script.addEventListener('error', handleError);
    document.head.appendChild(script);

    // Cleanup
    return () => {
      script.removeEventListener('load', handleLoad);
      script.removeEventListener('error', handleError);
    };
  }, [src]);
};

export default useScript;
